import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { buildTableQueryString, ITableQueryParams } from "../redux";
import { api } from "../api/api";

interface IUseTableQueryDataReturn<T> {
  data: T[];
  totalRecords: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useCustomTableQueryData<T = any>(
  endpoint: string,
  params: ITableQueryParams,
): IUseTableQueryDataReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(null);

      const query = buildTableQueryString(params);

      const response = await api.get(`${endpoint}${query}`, {
        signal: controller.signal,
      });

      // 🔥 Extract properly
      const result = response?.data?.data ?? [];
      const total = response?.data?.totalRecords ?? 0;

      setData(result);
      setTotalRecords(total);
    } catch (err: any) {
      if (err?.name === "CanceledError") return;

      const message =
        err?.response?.data?.message || err?.message || "Something went wrong";

      setError(message);
    } finally {
      setLoading(false);
    }
  }, [endpoint, params]);

  useEffect(() => {
    fetchData();

    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);

  return {
    data,
    totalRecords,
    loading,
    error,
    refetch: fetchData,
  };
}

export function useTableQueryWrapper<T = any>(
  endpoint: string,
  params: ITableQueryParams,
): IUseTableQueryDataReturn<T> {
  const memoParams = useMemo(() => {
    return params;
  }, [JSON.stringify(params)]);

  return useCustomTableQueryData(endpoint, memoParams);
}
